import React, { useState, useCallback, useEffect } from 'react';
import { behavior } from '../../analytics';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UrlInputStep from '../../components/onboarding/UrlInputStep';
import PlanningVisualizationStep from '../../components/onboarding/PlanningVisualizationStep';
import AnalysisReportStep from '../../components/onboarding/AnalysisReportStep';
import EmailCollectionModal from '../../components/onboarding/EmailCollectionModal';
import BannerGrid30Step from '../../components/onboarding/BannerGrid30Step';
import PlanSelectionStep from '../../components/onboarding/PlanSelectionStep';
import Image from '../../components/common/Image';
import { collectEmail } from '../../services/api/onboardingService';
import userService from '../../services/api/userService';
import type { OnboardingAnalysisResult, PlanningData, OnboardingState } from '../../types/onboarding';
import { tokenManager } from '../../utils/tokenManager';
import { trackMetaAddPaymentInfo, trackMetaStartTrial } from '../../utils/metaPixel';
import { trackXAddPaymentInfo } from '../../utils/xPixel';

// New onboarding v2 flow steps
type OnboardingStep =
  | 'url-input'
  | 'planning'
  | 'report'
  | 'email'
  | 'banner-grid'
  | 'plan-selection'
  | 'complete';

// Visual step indicator mapping
interface VisualStep {
  label: string;
  internalSteps: OnboardingStep[];
}

const VISUAL_STEPS: VisualStep[] = [
  { label: 'URL入力', internalSteps: ['url-input'] },
  { label: 'AI分析', internalSteps: ['planning', 'report'] },
  { label: 'バナー生成', internalSteps: ['email', 'banner-grid'] },
  { label: 'プラン選択', internalSteps: ['plan-selection', 'complete'] },
];

function getVisualStepIndex(currentStep: OnboardingStep): number {
  for (let i = 0; i < VISUAL_STEPS.length; i++) {
    if (VISUAL_STEPS[i].internalSteps.includes(currentStep)) {
      return i;
    }
  }
  return 0;
}

// --- Tailwind class constants (旧 styled-components) ---
// keyframes は globals.css の `obc-fade-in-up-10` へ移設済み。

const PAGE_CONTAINER =
  'min-h-screen flex flex-col items-center justify-center bg-warm-bg p-4';

const CARD_BASE =
  'w-full bg-white rounded-xl shadow-[0_0_18px_0_rgba(31,23,18,0.08)] p-8 transition-[max-width] duration-300 ease-[ease]';
const CARD_WIDE = 'max-w-[1100px]';
const CARD_NARROW = 'max-w-[600px]';

const LOGO =
  'flex justify-center items-center mb-6 [&_img]:h-[60px] [&_img]:w-auto';

const STEP_INDICATOR_CONTAINER = 'flex justify-center items-center mb-8 px-4';

const STEP_ITEM = 'flex flex-col items-center shrink-0';

const STEP_DOT_BASE =
  'w-3 h-3 rounded-full border-2 transition-all duration-300 ease-[ease] relative';
const STEP_DOT_ACTIVE =
  'bg-primary border-primary shadow-[0_0_0_4px_rgba(97,111,201,0.2)]';
const STEP_DOT_COMPLETED = 'bg-primary border-primary';
const STEP_DOT_IDLE = 'bg-gray-200 border-gray-200';

const STEP_LABEL_BASE = 'text-[0.7rem] mt-1.5 whitespace-nowrap';
const STEP_LABEL_ACTIVE = 'text-primary font-semibold';
const STEP_LABEL_COMPLETED = 'text-primary font-normal';
const STEP_LABEL_IDLE = 'text-gray-400 font-normal';

const STEP_CONNECTOR_BASE =
  'w-10 h-0.5 transition-all duration-300 ease-[ease] mx-1 mb-5';
const STEP_CONNECTOR_COMPLETED = 'bg-primary';
const STEP_CONNECTOR_IDLE = 'bg-gray-200';

const STEP_CONTENT = 'animate-[obc-fade-in-up-10_0.3s_ease-out]';

const ERROR_BANNER =
  'bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-red-600 text-[0.875rem]';

const PLACEHOLDER_STEP = 'text-center py-8 text-gray-500';

const PLACEHOLDER_TITLE = 'text-[1.25rem] text-gray-700 mb-2';

const PLACEHOLDER_TEXT = 'text-[0.875rem] text-gray-400';

const COMPLETE_ACTION_BUTTON =
  'bg-transparent border border-gray-300 text-gray-500 text-[0.875rem] py-2 px-5 rounded-md cursor-pointer mt-4 transition-all duration-200 ease-[ease] hover:border-primary hover:text-primary';

// Steps that should hide the indicator
const HIDDEN_INDICATOR_STEPS: OnboardingStep[] = ['planning', 'complete'];

// Steps that need a wider card
const WIDE_CARD_STEPS: OnboardingStep[] = ['banner-grid', 'plan-selection', 'report'];

// Extended state for v2
interface OnboardingV2State extends OnboardingState {
  planningData: PlanningData | null;
  batchSessionId: string | null;
}

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('url-input');

  // 行動分析: ステップ到達を記録（admin 行動分析タブの活性化ファネルが参照）
  useEffect(() => {
    behavior.step('onboarding', currentStep, { variant: 'v1' });
  }, [currentStep]);
  const [error, setError] = useState<string | null>(null);

  const [state, setState] = useState<OnboardingV2State>({
    serviceUrl: '',
    analysisResult: null,
    bannerSession: null,
    banners: [],
    userEmail: null,
    leadId: null,
    planningData: null,
    batchSessionId: null,
  });

  const visualIndex = getVisualStepIndex(currentStep);

  useEffect(() => {
    if (searchParams.get('step') !== 'complete') return;

    const sessionId = searchParams.get('session_id');
    const plan = searchParams.get('plan') || 'personal';
    const trialParam = searchParams.get('trial');
    const parsedTrialDays = trialParam ? Number(trialParam) : undefined;

    const addPaymentEventId = trackMetaAddPaymentInfo({ plan, sessionId });
    trackXAddPaymentInfo(addPaymentEventId, plan);
    trackMetaStartTrial({
      plan,
      trialDays: Number.isFinite(parsedTrialDays) ? parsedTrialDays : undefined,
      sessionId,
    });

    setCurrentStep('complete');

    const next = new URLSearchParams(searchParams);
    next.delete('step');
    next.delete('session_id');
    next.delete('plan');
    next.delete('trial');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Step 1: URL Input -> Planning
  const handleUrlComplete = useCallback((url: string) => {
    setState(prev => ({ ...prev, serviceUrl: url }));
    setError(null);
    setCurrentStep('planning');
  }, []);

  // Step 2: Planning -> Report
  const handlePlanningComplete = useCallback((planningData: PlanningData, analysisResult: OnboardingAnalysisResult, leadId: number, batchSessionId?: string) => {
    setState(prev => ({
      ...prev,
      planningData,
      analysisResult,
      leadId,
      batchSessionId: batchSessionId || null,
    }));

    setCurrentStep('report');
  }, []);

  // Step 3: Report -> Email or Banner Grid
  const handleReportReceive = useCallback(() => {
    setCurrentStep('email');
  }, []);

  const handleReportContinue = useCallback(() => {
    setCurrentStep('banner-grid');
  }, []);

  // Step 4: Email -> Banner Grid
  const handleEmailComplete = useCallback(async (email: string | null) => {
    setState(prev => ({ ...prev, userEmail: email }));

    if (email) {
      try {
        await collectEmail({ email, lead_id: state.leadId });
      } catch {
        // Non-blocking
      }
    }

    setCurrentStep('banner-grid');
  }, [state.leadId]);

  // Step 5: Banner Grid -> Plan Selection
  const handleBannerGridContinue = useCallback(() => {
    setCurrentStep('plan-selection');
  }, []);

  // Step 6: Plan Selection skip -> dashboard
  const handlePlanSkip = useCallback(async () => {
    if (tokenManager.getToken()) {
      try {
        await userService.completeOnboarding();
      } catch {
        // Non-blocking: navigation should continue even if the session expired.
      }
    }

    navigate('/');
  }, [navigate]);

  // Step 7: Complete -> Auto-redirect
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (currentStep !== 'complete') return;

    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentStep, navigate]);

  // --- Render ---

  const renderStep = () => {
    switch (currentStep) {
      case 'url-input':
        return (
          <UrlInputStep
            onNext={handleUrlComplete}
            initialUrl={state.serviceUrl}
          />
        );

      case 'planning':
        return (
          <PlanningVisualizationStep
            serviceUrl={state.serviceUrl}
            onComplete={(planningData, leadId, batchSessionId) => {
              const analysisResult: OnboardingAnalysisResult = {
                logo_url: planningData.brand_info.logo_url,
                og_image_url: planningData.brand_info.og_image_url,
                favicon_url: null,
                colors: planningData.brand_info.colors,
                title: planningData.product_features.title,
                description: planningData.ad_strategy.target_audience,
                category: planningData.competitor_info.industry,
                domain: null,
                logo_text_fallback: null,
              };
              handlePlanningComplete(planningData, analysisResult, leadId, batchSessionId);
            }}
            onBack={() => setCurrentStep('url-input')}
          />
        );

      case 'report':
        return state.planningData && state.analysisResult ? (
          <AnalysisReportStep
            planningData={state.planningData}
            analysisResult={state.analysisResult}
            onReceiveReport={handleReportReceive}
            onContinue={handleReportContinue}
          />
        ) : null;

      case 'email':
        return (
          <EmailCollectionModal
            onSubmit={(email) => handleEmailComplete(email)}
            onSkip={() => handleEmailComplete(null)}
          />
        );

      case 'banner-grid':
        return state.batchSessionId ? (
          <BannerGrid30Step
            sessionId={state.batchSessionId}
            onContinue={handleBannerGridContinue}
            onBack={() => setCurrentStep('report')}
          />
        ) : (
          <div className={PLACEHOLDER_STEP}>
            <h3 className={PLACEHOLDER_TITLE}>バナー生成の準備中...</h3>
            <p className={PLACEHOLDER_TEXT}>しばらくお待ちください</p>
            <button className={COMPLETE_ACTION_BUTTON} type="button" onClick={handleBannerGridContinue}>
              スキップして次へ
            </button>
          </div>
        );

      case 'plan-selection':
        return (
          <PlanSelectionStep
            email={state.userEmail}
            leadId={state.leadId}
            onSkip={handlePlanSkip}
          />
        );

      case 'complete':
        return (
          <div className={PLACEHOLDER_STEP}>
            <h3 className={PLACEHOLDER_TITLE}>セットアップ完了</h3>
            <p className={PLACEHOLDER_TEXT}>
              {countdown}秒後にダッシュボードへ移動します...
            </p>
            <button className={COMPLETE_ACTION_BUTTON} type="button" onClick={() => navigate('/')}>
              今すぐ移動
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  const showIndicator = !HIDDEN_INDICATOR_STEPS.includes(currentStep);
  const isWide = WIDE_CARD_STEPS.includes(currentStep);

  return (
    <div className={`onboarding-page ${PAGE_CONTAINER}`}>
      <div className={`onboarding-page__card ${CARD_BASE} ${isWide ? CARD_WIDE : CARD_NARROW}`}>
        <div className={`onboarding-page__logo ${LOGO}`}>
          <Image category="common" fileName="logo.png" alt="Tasky" />
        </div>

        {showIndicator && (
          <div className={`onboarding-page__step-indicator ${STEP_INDICATOR_CONTAINER}`}>
            {VISUAL_STEPS.map((step, index) => {
              const isActive = visualIndex === index;
              const isCompleted = visualIndex > index;

              return (
                <React.Fragment key={step.label}>
                  {index > 0 && (
                    <div
                      className={`${STEP_CONNECTOR_BASE} ${
                        visualIndex > index - 1 ? STEP_CONNECTOR_COMPLETED : STEP_CONNECTOR_IDLE
                      }`}
                    />
                  )}
                  <div className={STEP_ITEM}>
                    <div
                      className={`${STEP_DOT_BASE} ${
                        isActive
                          ? STEP_DOT_ACTIVE
                          : isCompleted
                            ? STEP_DOT_COMPLETED
                            : STEP_DOT_IDLE
                      }`}
                    />
                    <span
                      className={`${STEP_LABEL_BASE} ${
                        isActive
                          ? STEP_LABEL_ACTIVE
                          : isCompleted
                            ? STEP_LABEL_COMPLETED
                            : STEP_LABEL_IDLE
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {error && (
          <div className={`onboarding-page__error ${ERROR_BANNER}`}>
            {error}
          </div>
        )}

        <div className={STEP_CONTENT} key={currentStep}>
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
